import { useEffect, useRef, useState, useCallback } from 'react';
import { Button } from './ui/button';
import { Camera, X, SwitchCamera, Upload, AlertCircle } from 'lucide-react';

export default function BarcodeScanner({ onScan, onClose }) {
  const scannerRef = useRef(null);
  const [error, setError] = useState('');
  const [cameras, setCameras] = useState([]);
  const [activeCameraIdx, setActiveCameraIdx] = useState(0);
  const [scanning, setScanning] = useState(false);
  const [ready, setReady] = useState(false);
  const fileInputRef = useRef(null);

  const stopScanner = useCallback(async () => {
    if (scannerRef.current) {
      try {
        const state = scannerRef.current.getState();
        if (state === 2 || state === 3) { // SCANNING or PAUSED
          await scannerRef.current.stop();
        }
      } catch {
        // Scanner wasn't running
      }
    }
  }, []);

  const startScanner = useCallback(async (cameraId) => {
    if (!scannerRef.current) return;
    try {
      setError('');
      await stopScanner();
      setScanning(true);
      await scannerRef.current.start(
        cameraId,
        { fps: 10, qrbox: { width: 220, height: 220 }, aspectRatio: 1.0 },
        (decodedText) => {
          stopScanner();
          setScanning(false);
          onScan(decodedText);
        },
        () => {} // ignore per-frame errors
      );
    } catch (err) {
      setScanning(false);
      setError('Camera access denied or unavailable. Try uploading an image instead.');
    }
  }, [onScan, stopScanner]);

  useEffect(() => {
    let mounted = true;
    let instance = null;

    const init = async () => {
      try {
        const { Html5Qrcode } = await import('html5-qrcode');

        if (!mounted) return;
        instance = new Html5Qrcode('barcode-scanner-view');
        scannerRef.current = instance;
        setReady(true);

        try {
          const devices = await Html5Qrcode.getCameras();
          if (!mounted) return;

          if (devices && devices.length > 0) {
            setCameras(devices);
            const backIdx = devices.findIndex(d =>
              d.label.toLowerCase().includes('back') ||
              d.label.toLowerCase().includes('rear') ||
              d.label.toLowerCase().includes('environment')
            );
            const idx = backIdx >= 0 ? backIdx : 0;
            setActiveCameraIdx(idx);
            startScanner(devices[idx].id);
          } else {
            setError('No cameras found. Use "Upload Image" to scan from a photo.');
          }
        } catch {
          if (mounted) {
            setError('Camera permission denied. Use "Upload Image" to scan from a photo.');
          }
        }
      } catch (err) {
        if (mounted) {
          setError('Scanner failed to initialize. Use "Upload Image" to scan from a photo.');
        }
      }
    };

    init();

    return () => {
      mounted = false;
      if (instance) {
        try {
          const state = instance.getState();
          if (state === 2 || state === 3) {
            instance.stop().catch(() => {});
          }
        } catch {
          // ignore
        }
        try {
          instance.clear();
        } catch {
          // ignore
        }
      }
    };
  }, [startScanner]);

  const switchCamera = async () => {
    if (cameras.length <= 1) return;
    const nextIdx = (activeCameraIdx + 1) % cameras.length;
    setActiveCameraIdx(nextIdx);
    startScanner(cameras[nextIdx].id);
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!scannerRef.current) {
      // If scanner didn't initialize, try dynamic import for file scanning
      try {
        const { Html5Qrcode } = await import('html5-qrcode');
        const tempScanner = new Html5Qrcode('barcode-scanner-view');
        const result = await tempScanner.scanFile(file, true);
        tempScanner.clear().catch(() => {});
        onScan(result);
        return;
      } catch {
        setError('Could not read any code from this image. Try a clearer photo.');
        return;
      }
    }

    try {
      await stopScanner();
      setScanning(false);
      const result = await scannerRef.current.scanFile(file, true);
      onScan(result);
    } catch {
      setError('Could not read any code from this image. Try a clearer photo.');
    }
    // Reset file input
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  return (
    <div className="fixed inset-0 z-[100] bg-black/80 flex items-center justify-center p-4" data-testid="barcode-scanner-overlay">
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 bg-gray-50">
          <div className="flex items-center gap-2">
            <Camera className="w-5 h-5 text-blue-600" />
            <span className="font-semibold text-gray-900 text-sm">Scan QR / Barcode</span>
          </div>
          <button onClick={onClose} className="p-1.5 hover:bg-gray-200 rounded-lg transition-colors" data-testid="scanner-close-btn">
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Scanner view */}
        <div className="relative bg-black" style={{ minHeight: scanning ? 300 : 120 }}>
          <div id="barcode-scanner-view" className="w-full" />
          {!scanning && !error && (
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="text-gray-400 text-sm animate-pulse">Initializing camera...</div>
            </div>
          )}
          {!scanning && error && (
            <div className="absolute inset-0 flex items-center justify-center p-4">
              <div className="text-center">
                <AlertCircle className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                <div className="text-gray-400 text-sm">Camera unavailable</div>
              </div>
            </div>
          )}
        </div>

        {/* Controls */}
        <div className="p-4 space-y-3">
          {error && (
            <div className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-3" data-testid="scanner-error">
              {error}
            </div>
          )}

          <div className="flex gap-2">
            {cameras.length > 1 && (
              <Button variant="outline" size="sm" onClick={switchCamera} className="flex-1 border-gray-300 text-gray-700" data-testid="scanner-switch-camera">
                <SwitchCamera className="w-4 h-4 mr-1.5" /> Switch Camera
              </Button>
            )}
            <Button variant="outline" size="sm" onClick={() => fileInputRef.current?.click()}
              className="flex-1 border-gray-300 text-gray-700" data-testid="scanner-upload-btn">
              <Upload className="w-4 h-4 mr-1.5" /> Upload Image
            </Button>
            <input ref={fileInputRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={handleFileUpload} />
          </div>

          <p className="text-xs text-gray-400 text-center">
            Point camera at a QR code or barcode, or upload a photo to scan.
          </p>
        </div>
      </div>
    </div>
  );
}
